"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";

interface YearPickerProps {
	year?: number;
	onYearChange?: (year: number) => void;
	className?: string;
	maxYear?: number;
}

export function YearPicker({
	year = new Date().getFullYear(),
	onYearChange,
	maxYear = new Date().getFullYear(),
}: YearPickerProps) {
	const [selectedYear, setSelectedYear] = useState<number>(year);
	const [yearRange, setYearRange] = useState<number>(
		Math.floor(year / 12) * 12,
	);

	// Generate array of years for the current range
	const years = useMemo(() => {
		return Array.from({ length: 12 }, (_, i) => yearRange + i);
	}, [yearRange]);

	const handleYearSelect = (year: number) => {
		setSelectedYear(year);
		onYearChange?.(year);
	};

	const handlePreviousYears = () => {
		setYearRange(yearRange - 12);
	};

	const handleNextYears = () => {
		setYearRange(yearRange + 12);
	};

	return (
		<div className="space-y-4 p-3">
			<div className="flex items-center justify-between">
				<Button
					variant="outline"
					size="icon"
					className="h-7 w-7"
					onClick={handlePreviousYears}
				>
					<ChevronLeft className="h-4 w-4" />
					<span className="sr-only">Previous years</span>
				</Button>
				<div className="text-sm font-medium">
					{yearRange} - {yearRange + 11}
				</div>
				<Button
					variant="outline"
					size="icon"
					className="h-7 w-7"
					onClick={handleNextYears}
				>
					<ChevronRight className="h-4 w-4" />
					<span className="sr-only">Next years</span>
				</Button>
			</div>
			<div className="grid grid-cols-3 gap-2">
				{years.map((year) => (
					<Button
						key={year}
						variant={year === selectedYear ? "default" : "outline"}
						className="h-9"
						onClick={() => handleYearSelect(year)}
						disabled={year > maxYear}
					>
						{year}
					</Button>
				))}
			</div>
		</div>
	);
}
